import React, { useState, useEffect, useCallback } from "react";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader/Loader";
import SlidePanel from "../../../components/SlidePanel/SlidePanel";
import * as workflowService from "../../../services/workflowService";
import useSkills from "../../../hooks/useSkills";
import FlowPreview from "./workflows/FlowPreview";
import WorkflowBuilder from "./workflows/WorkflowBuilder";
import WorkflowServicesPanel from "./workflows/WorkflowServicesPanel";

const cardStyle = { background: "#fff", borderRadius: "12px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", padding: "16px" };

function WorkflowCard({ template, skills, resourceLabel, usedBy, onEdit, onServices, onRemove }) {
  return (
    <div className="mb-3" style={cardStyle} data-testid="workflow-card">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
        <div>
          <div style={{ fontWeight: 600, color: "#111827" }}>{template.name}</div>
          <div style={{ fontSize: "12px", color: "#6B7280" }}>
            {usedBy.length ? `Used for: ${usedBy.join(", ")}` : "Not used for any service yet"}
          </div>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onServices(template)}>
            Services
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onEdit(template)}>
            Edit
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onRemove(template)}>
            Remove
          </button>
        </div>
      </div>
      <FlowPreview steps={template.steps} skills={skills} resourceLabel={resourceLabel} />
    </div>
  );
}

function StarterCard({ template, skills, resourceLabel, busy, onUse }) {
  return (
    <div className="mb-3" style={{ ...cardStyle, border: "1px dashed #F5C2D3" }} data-testid="starter-card">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
        <div>
          <div style={{ fontWeight: 600, color: "#111827" }}>{template.name}</div>
          {template.description && <div style={{ fontSize: "12px", color: "#6B7280" }}>{template.description}</div>}
        </div>
        <button type="button" className="btn btn-sm btn-danger" disabled={busy} onClick={() => onUse(template)}>
          {busy ? <Loader style={{ width: 30, height: 16 }} /> : "Use this"}
        </button>
      </div>
      <FlowPreview steps={template.steps} skills={skills} resourceLabel={resourceLabel} />
    </div>
  );
}

function RemovePanel({ template, busy, onCancel, onConfirm }) {
  return (
    <SlidePanel show={!!template} onHide={onCancel} title="Remove workflow">
      <p style={{ fontSize: "15px", color: "#374151" }}>
        Remove <strong>{template?.name}</strong>? New bookings will no longer follow it. Bookings already made with it
        keep their steps.
      </p>
      <div className="d-flex gap-3 mt-4">
        <button onClick={onCancel} className="btn btn-outline-secondary px-4">
          Cancel
        </button>
        <button onClick={onConfirm} className="btn Button primaryBackground text-white px-4" disabled={busy}>
          {busy ? <Loader style={{ width: 40, height: 20 }} /> : "Remove"}
        </button>
      </div>
    </SlidePanel>
  );
}

// Setup > Workflows. A workflow is the list of steps a booking goes through (who
// does each, what is required, what happens when). Build one from a ready-made
// starter or from scratch, then tick the service categories that use it.
// `resourceLabel` is the industry's word for a resource, chosen by the parent.
function WorkflowsTab({ resourceLabel }) {
  const { skills } = useSkills();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const [builder, setBuilder] = useState({ show: false, editing: null });
  const [servicesFor, setServicesFor] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [removingBusy, setRemovingBusy] = useState(false);
  const [usingStarter, setUsingStarter] = useState(null);
  const [showAllStarters, setShowAllStarters] = useState(false);

  // vendorProfile (not userDetails, which has no industry) - the starters for this
  // vendor's own industry are listed first.
  let industryId = null;
  try {
    const raw = localStorage.getItem("vendorProfile");
    industryId = raw && raw !== "undefined" ? Number(JSON.parse(raw)?.industry) || null : null;
  } catch (e) {
    industryId = null;
  }

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [t, c, l] = await Promise.all([
        workflowService.getWorkflowTemplates(),
        workflowService.getServiceCategories().catch(() => null),
        workflowService.getWorkflowLinks().catch(() => null),
      ]);
      setTemplates(t?.success ? t.data : []);
      setCategories((c?.data || []).map((s) => ({ id: s.service_id ?? s.id, name: s.service_name ?? s.name })));
      setLinks(l?.success ? l.data : []);
    } catch (e) {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const own = templates.filter((t) => !t.is_starter);
  const starters = templates
    .filter((t) => t.is_starter)
    .sort((a, b) => Number(b.industry_id === industryId) - Number(a.industry_id === industryId));
  const forMyIndustry = starters.filter((t) => t.industry_id === industryId);
  const shownStarters = showAllStarters || forMyIndustry.length === 0 ? starters : forMyIndustry;

  const templateNames = Object.fromEntries(own.map((t) => [t.id, t.name]));
  const categoryName = (id) => categories.find((c) => Number(c.id) === Number(id))?.name;
  const usedByOf = (t) => [
    ...t.category_ids.map(categoryName).filter(Boolean),
  ].concat(t.offering_ids.length ? [`${t.offering_ids.length} service type${t.offering_ids.length > 1 ? "s" : ""}`] : []);

  const useStarter = async (starter) => {
    setUsingStarter(starter.starter_key);
    try {
      const res = await workflowService.cloneStarterWorkflow(starter.starter_key);
      if (!res?.success) return toast.error(workflowService.resultMessage(res));
      const fresh = await workflowService.getWorkflowTemplate(res.data.id);
      await load();
      // Straight into editing the copy, so the vendor can make it theirs.
      setBuilder({ show: true, editing: fresh?.success ? fresh.data : { id: res.data.id, name: res.data.name, steps: starter.steps } });
    } catch (e) {
      toast.error("Could not create the workflow");
    } finally {
      setUsingStarter(null);
    }
  };

  const confirmRemove = async () => {
    setRemovingBusy(true);
    try {
      const res = await workflowService.removeWorkflowTemplate(removing.id);
      if (res?.success) {
        toast.success("Workflow removed");
        setRemoving(null);
        load();
      } else {
        toast.error(workflowService.resultMessage(res));
      }
    } finally {
      setRemovingBusy(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-1 fw-bold" style={{ color: "#111827" }}>
            Workflows
          </h5>
          <p className="mb-0" style={{ fontSize: "13px", color: "#6B7280" }}>
            The steps a booking goes through: who does each one, what is required and what happens in what order. Build
            one once and use it for as many services as you like. Bookings can still be made with nobody assigned.
          </p>
        </div>
        <button
          onClick={() => setBuilder({ show: true, editing: null })}
          className="btn btn-danger"
          style={{ borderRadius: "8px", fontSize: "13px", fontWeight: 600, height: "38px" }}
        >
          + New workflow
        </button>
      </div>

      {loadFailed && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center" role="alert">
          <span>Could not load workflows.</span>
          <button className="btn btn-sm btn-outline-secondary" onClick={load}>
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <Skeleton count={3} height={70} className="mb-3" />
      ) : (
        <>
          {own.length === 0 ? (
            <div className="mb-3 text-center" style={{ ...cardStyle, color: "#6B7280", padding: "32px" }}>
              You have no workflows yet. Start from a ready-made one below, or build your own.
            </div>
          ) : (
            own.map((t) => (
              <WorkflowCard
                key={t.id}
                template={t}
                skills={skills}
                resourceLabel={resourceLabel}
                usedBy={usedByOf(t)}
                onEdit={(tpl) => setBuilder({ show: true, editing: tpl })}
                onServices={setServicesFor}
                onRemove={setRemoving}
              />
            ))
          )}

          {starters.length > 0 && (
            <>
              <h6 className="mt-4 mb-2" style={{ fontWeight: 700, color: "#111827" }}>
                Start from a ready-made workflow
              </h6>
              {shownStarters.map((t) => (
                <StarterCard
                  key={t.id}
                  template={t}
                  skills={skills}
                  resourceLabel={resourceLabel}
                  busy={usingStarter === t.starter_key}
                  onUse={useStarter}
                />
              ))}
              {forMyIndustry.length > 0 && starters.length > forMyIndustry.length && (
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowAllStarters((v) => !v)}>
                  {showAllStarters ? "Show only the ones for my business" : "Show ready-made workflows for other businesses"}
                </button>
              )}
            </>
          )}
        </>
      )}

      <WorkflowBuilder
        show={builder.show}
        editing={builder.editing}
        skills={skills}
        resourceLabel={resourceLabel}
        onHide={() => setBuilder({ show: false, editing: null })}
        onSaved={() => {
          setBuilder({ show: false, editing: null });
          load();
        }}
      />
      <WorkflowServicesPanel
        show={!!servicesFor}
        template={servicesFor}
        categories={categories}
        links={links}
        templateNames={templateNames}
        onHide={() => setServicesFor(null)}
        onSaved={() => {
          setServicesFor(null);
          load();
        }}
      />
      <RemovePanel template={removing} busy={removingBusy} onCancel={() => setRemoving(null)} onConfirm={confirmRemove} />
    </div>
  );
}

export default WorkflowsTab;
